import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtRM, fmtDate } from "../../lib/format";
import { PageTitle, StatusBadge, Spinner } from "../../components/Shell";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";

export default function Subscription() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/tenant/subscription").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const checkout = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setBusy("");
    }
  };

  if (!data) return <Spinner />;
  const { subscription: sub, plan, invoices } = data;

  return (
    <div data-testid="tenant-subscription">
      <PageTitle title="Langganan" sub="Urus pelan dan pembayaran koperasi anda" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-brand-dark rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-gold">Pelan Semasa</div>
          <div className="mt-3 font-display text-3xl font-extrabold uppercase">{sub?.plan}</div>
          <div className="mt-2"><StatusBadge status={sub?.status} /></div>
          <div className="mt-5 text-sm text-emerald-100/70 space-y-1.5">
            <div>Kitaran: <span className="text-white font-semibold">{sub?.cycle === "annual" ? "Tahunan" : "Bulanan"}</span></div>
            <div>Sah sehingga: <span className="text-white font-semibold">{fmtDate(sub?.current_period_end)}</span></div>
            {plan && <div>Had ahli: <span className="text-white font-semibold">{plan.member_limit === -1 ? "Tanpa had" : plan.member_limit.toLocaleString()}</span></div>}
          </div>
        </div>
        <div className="lg:col-span-2 bg-card rounded-2xl border border-brand-deep/10 p-6">
          <h3 className="font-display font-semibold text-brand-dark mb-1">Aktifkan / Naik Taraf dengan Stripe</h3>
          <p className="text-xs text-muted-foreground mb-5">Pembayaran kad selamat melalui Stripe. Invois dijana automatik.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {["starter", "professional", "business"].map((code) => {
              const p = { starter: [299, 2990], professional: [799, 7990], business: [2000, 20000] }[code];
              return (
                <div key={code} className={`rounded-xl border-2 p-4 ${sub?.plan === code ? "border-gold bg-gold-light/40" : "border-brand-deep/10"}`} data-testid={`sub-plan-${code}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-brand-deep">{code}</div>
                  <div className="font-display text-xl font-extrabold text-brand-dark mt-1">RM{p[0].toLocaleString()}<span className="text-xs font-medium text-muted-foreground">/bln</span></div>
                  <div className="text-xs text-muted-foreground">atau RM{p[1].toLocaleString()}/thn</div>
                  <div className="mt-3 space-y-2">
                    <button data-testid={`subscribe-${code}-monthly`} onClick={() => checkout(`${code}_monthly`)} disabled={!!busy}
                      className="w-full bg-brand-deep text-white text-xs font-bold rounded-lg py-2 hover:bg-brand-dark transition-colors disabled:opacity-50">
                      {busy === `${code}_monthly` ? "Memproses..." : "Bulanan"}
                    </button>
                    <button data-testid={`subscribe-${code}-annual`} onClick={() => checkout(`${code}_annual`)} disabled={!!busy}
                      className="w-full border border-gold text-gold text-xs font-bold rounded-lg py-2 hover:bg-gold hover:text-brand-dark transition-colors disabled:opacity-50">
                      {busy === `${code}_annual` ? "Memproses..." : "Tahunan"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-6 bg-card rounded-xl border border-brand-deep/10 overflow-hidden">
        <div className="px-5 py-4 border-b border-brand-deep/10 font-display font-semibold text-brand-dark">Sejarah Invois</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-deep/5">
              <th className="px-5 py-2.5">No.</th><th className="px-5 py-2.5">Keterangan</th><th className="px-5 py-2.5">Tarikh</th><th className="px-5 py-2.5 text-right">Amaun</th><th className="px-5 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-brand-deep/5" data-testid={`sub-invoice-${inv.number}`}>
                <td className="px-5 py-3 font-mono text-xs">{inv.number}</td>
                <td className="px-5 py-3 text-xs">{inv.description}</td>
                <td className="px-5 py-3 text-xs">{fmtDate(inv.issued_at)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-brand-deep">{fmtRM(inv.amount)}</td>
                <td className="px-5 py-3"><StatusBadge status={inv.status} /></td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-muted-foreground">Tiada invois lagi.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
